import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ItemDetail } from '../routes/ItemDetail';

/**
 * Item detail rendered as a full-viewport overlay on top of Browse.
 *
 * Why: when the customer taps a card from Browse, we want them to see the
 * item without losing the scroll position they reached on Browse. By
 * rendering the item as a modal portal (with the Routes underneath still
 * matched against /browse) Browse never unmounts, so its scroll, page
 * count, and filter state are all preserved for free.
 *
 * The URL still changes to /browse/:slug, so links remain shareable and
 * a direct visit to that URL renders the full ItemDetail page (no
 * background location → no modal, just the page).
 *
 * Closing: the X button calls navigate(-1), which pops the modal off
 * the history stack and returns us to /browse with state intact. The
 * browser back button does the exact same thing.
 */
export function ItemModal() {
  const navigate = useNavigate();

  // Lock body scroll while the modal is open so iOS doesn't rubber-band
  // the page underneath when the user swipes inside the modal.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Esc closes the modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') navigate(-1);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  return createPortal(
    <div className="item-modal-root" role="dialog" aria-modal="true">
      <div
        className="item-modal-backdrop"
        onClick={() => navigate(-1)}
        aria-hidden="true"
      />
      <div className="item-modal-sheet">
        <button
          type="button"
          className="item-modal-close"
          onClick={() => navigate(-1)}
          aria-label="Close"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <ItemDetail />
      </div>
    </div>,
    document.body
  );
}
